import { forwardRef } from 'react';

import { cx } from 'cva';

import { Button } from '../button/button';
import { Icon } from '../icon/icon';
import { Stack } from '../stack/stack';
import { Tooltip } from '../tooltip/tooltip';
import { TooltipTrigger } from '../tooltip_trigger/tooltip_trigger';

import styles from './panel.module.css';

export type PanelHeaderProps = React.PropsWithChildren<
  React.HTMLAttributes<HTMLDivElement>
> & {
  /** Show a close button */
  isClosable?: boolean;
  /** Callback when close button is clicked */
  onClose?: () => void;
};

/**
 * PanelHeader - container for heading / actions.
 * Accepts `isClosable` + `onClose` to show a close button.
 */
export const PanelHeader = forwardRef<HTMLDivElement, PanelHeaderProps>(
  ({ className, children, isClosable, onClose, ...rest }, ref) => {
    const classes = cx([styles.panel_header, className]);
    return (
      <Stack
        elementType="header"
        direction="row"
        alignItems="center"
        className={classes}
        ref={ref}
        {...rest}
      >
        {children}
        {isClosable && (
          <TooltipTrigger>
            <Tooltip>Close panel</Tooltip>
            <Button
              aria-label="Close panel"
              variant="tertiary"
              size="m"
              className={styles.close_button}
              onPress={onClose}
              icon={<Icon name="close" />}
            />
          </TooltipTrigger>
        )}
      </Stack>
    );
  },
);
